import { Box, Container, Typography, Stack, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useLanguage } from '../contexts/LanguageContext';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: '120px 0',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)'
    : 'linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)',
  position: 'relative',
}));

const ProductCard = styled(Box)(({ theme }) => ({
  borderRadius: '32px',
  overflow: 'hidden',
  background: theme.palette.mode === 'dark'
    ? 'rgba(30, 41, 59, 0.8)'
    : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 10px 40px rgba(0, 0, 0, 0.3)'
    : '0 10px 40px rgba(0, 0, 0, 0.08)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.secondary.main}08 100%)`,
    opacity: 0,
    transition: 'opacity 0.6s ease',
    zIndex: 0,
  },
  '&:hover': {
    transform: 'translateY(-12px) scale(1.01)',
    border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(37, 99, 235, 0.3)'}`,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 40px 100px rgba(16, 185, 129, 0.3), 0 0 80px rgba(37, 99, 235, 0.2)'
      : '0 40px 100px rgba(37, 99, 235, 0.25), 0 0 80px rgba(16, 185, 129, 0.15)',
    '&::before': {
      opacity: 1,
    },
    '& .product-image': {
      transform: 'scale(1.08)',
    },
    '& .feature-badge': {
      transform: 'translateY(-8px) scale(1.05)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 24px rgba(16, 185, 129, 0.4)'
        : '0 8px 24px rgba(37, 99, 235, 0.3)',
    },
  },
}));

const ProductImage = styled('img')({
  width: '100%',
  height: '450px',
  objectFit: 'cover',
  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
});

const ImageOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.1) 0%, rgba(15, 23, 42, 0.4) 100%)'
    : 'linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.3) 100%)',
  pointerEvents: 'none',
  zIndex: 1,
}));

const FeatureBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '24px',
  right: '24px',
  padding: '12px 24px',
  borderRadius: '16px',
  background: theme.palette.mode === 'dark'
    ? 'rgba(16, 185, 129, 0.2)'
    : 'rgba(37, 99, 235, 0.15)',
  backdropFilter: 'blur(12px)',
  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(37, 99, 235, 0.3)'}`,
  color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.primary.main,
  fontWeight: 700,
  fontSize: '0.9rem',
  zIndex: 2,
  transition: 'all 0.4s ease',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 16px rgba(16, 185, 129, 0.2)'
    : '0 4px 16px rgba(37, 99, 235, 0.15)',
}));

const ProductButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: '#FFFFFF',
  padding: '14px 32px',
  borderRadius: '14px',
  fontWeight: 700,
  fontSize: '1rem',
  textTransform: 'none',
  border: 'none',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 24px rgba(16, 185, 129, 0.3)'
    : '0 8px 24px rgba(37, 99, 235, 0.25)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
    transition: 'left 0.6s ease',
  },
  '&:hover': {
    transform: 'translateY(-4px) scale(1.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 32px rgba(16, 185, 129, 0.4)'
      : '0 12px 32px rgba(37, 99, 235, 0.35)',
    '&::before': {
      left: '100%',
    },
  },
}));

const FeatureHighlight = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  borderRadius: '12px',
  background: theme.palette.mode === 'dark'
    ? 'rgba(16, 185, 129, 0.1)'
    : 'rgba(37, 99, 235, 0.08)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(37, 99, 235, 0.2)'}`,
  color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.primary.main,
  fontWeight: 600,
  fontSize: '0.95rem',
  marginTop: '8px',
}));

export default function ProductsSection() {
  const { t } = useLanguage();

  const products = [
    {
      title: t('products.beachbot.title'),
      description: t('products.beachbot.desc'),
      image: 'https://images.unsplash.com/photo-1568617781063-d91580fc30f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBiZWFjaCUyMGJhciUyMFZJUCUyMGJlYWNoJTIwY2x1YiUyMGVsZWdhbnQlMjBiZWFjaGZyb250JTIwY2FsbSUyMG9jZWFuJTIwdXBzY2FsZSUyMGxvdW5nZXxlbnwwfDB8fHwxNzU5Mjg5OTc2fDA&ixlib=rb-4.1.0&q=85',
      alt: 'Elegant luxury beach bar - Sophie Sanders on Unsplash (https://unsplash.com/@sophiesticat)',
      badge: '🏖️ Beach Management',
      highlights: ['Smart Automation', 'Real-time Monitoring', 'Cloud Integration'],
      link: '/products/beachbot',
    },
    {
      title: t('products.verilobi.title'),
      description: t('products.verilobi.desc'),
      image: 'https://images.unsplash.com/photo-1593407089396-93f0c7a575f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHw2fHxmYWNlJTIwcmVjb2duaXRpb24lMjBiaW9tZXRyaWMlMjBzZWN1cml0eSUyMGRpZ2l0YWwlMjB0ZWNobm9sb2d5fGVufDB8MHx8Ymx1ZXwxNzU5MzQzNjI2fDA&ixlib=rb-4.1.0&q=85',
      alt: 'Face recognition personal identification technology - Riku Lu on Unsplash (https://unsplash.com/@riku)',
      badge: '🔐 AI Security',
      highlights: ['Facial Recognition', 'Mobile App', 'Data Analytics'],
      link: '/products/verilobi',
    },
  ];

  return (
    <SectionContainer component="section" id="products" aria-labelledby="products-heading">
      <Container maxWidth="lg">
        <Stack spacing={3} alignItems="center" textAlign="center" sx={{ mb: 10 }}>
          <Typography
            id="products-heading"
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-16px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100px',
                height: '5px',
                borderRadius: '3px',
                background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              },
            }}
          >
            {t('products.title')}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 700, lineHeight: 1.8, fontSize: '1.15rem', mt: 3 }}
          >
            {t('products.subtitle')}
          </Typography>
        </Stack>

        <Stack spacing={6}>
          {products.map((product, index) => (
            <ProductCard key={index} className="product-card">
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: index % 2 === 0 ? '1.2fr 1fr' : '1fr 1.2fr' },
                  gap: 0,
                  minHeight: '450px',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    order: { xs: 1, md: index % 2 === 0 ? 1 : 2 },
                  }}
                >
                  <ProductImage 
                    src={product.image} 
                    alt={product.alt}
                    className="product-image"
                  />
                  <ImageOverlay />
                  <FeatureBadge className="feature-badge">
                    {product.badge}
                  </FeatureBadge>
                </Box>

                <Stack
                  spacing={3}
                  sx={{
                    p: { xs: 4, md: 6 },
                    justifyContent: 'center',
                    order: { xs: 2, md: index % 2 === 0 ? 2 : 1 },
                    position: 'relative',
                    zIndex: 2,
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      lineHeight: 1.2,
                    }}
                  >
                    {product.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ 
                      lineHeight: 1.9, 
                      fontSize: '1.1rem',
                      fontWeight: 400,
                    }}
                  >
                    {product.description}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {product.highlights.map((highlight, idx) => (
                      <FeatureHighlight key={idx}>
                        ✓ {highlight}
                      </FeatureHighlight>
                    ))}
                  </Stack>

                  <Box sx={{ pt: 2 }}>
                    <ProductButton 
                      component={Link}
                      to={product.link}
                      endIcon={<ArrowForwardIcon />}
                    >
                      {t('products.cta')}
                    </ProductButton>
                  </Box>
                </Stack>
              </Box>
            </ProductCard>
          ))}
        </Stack>
      </Container>
    </SectionContainer>
  );
}